import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';

import {
    delegationState,
    DnsZone,
    recordCountState,
} from '../types';

// FNP-1191 CRUD half — THE CLIENT-SIDE DELETE REFUSALS.
//
// ⚠️ TWO GUARDS LIVE IN TWO DIFFERENT PLACES, AND THAT IS DELIBERATE.
//
//   ZONE_NOT_EMPTY — the PROVIDER's refusal. Still not checked here. Route 53
//   is the authority on whether a zone holds records, and FNP-1219 declined to
//   pre-check it even server-side. It surfaces as a classified error after the
//   attempt, and FNP-1391 did NOT change that — see the note below.
//
//   DELEGATION — checked HERE, before the attempt. The provider has no opinion
//   about it: a delegated zone deletes perfectly happily at Route 53, and the
//   damage is entirely on Flexi's side of the boundary. Waiting for a refusal
//   that will never come would mean the zone is simply gone.
//
// So this file is not "the other half of the same check". It is the guard for
// the failure mode the provider will NOT protect anyone from.
//
// ─── FNP-1391: FNP-1219's DECISION IS NARROWED, NOT OVERTURNED ───
//
// FNP-1219 declined a ZONE_NOT_EMPTY pre-check for three reasons. FNP-1422
// landing `recordCount` on `DnsZone` changed what one of them costs, and left
// the other two standing. Taking them in order, because the difference between
// them is the whole design:
//
//   1. IT DUPLICATES A RULE THE PROVIDER OWNS. Softened, not gone. What this
//      file now returns is not a CHECK — it decides nothing and refuses
//      nothing. It is a DESCRIPTION of a field the zone already carries,
//      handed to the dialog so the dialog can say what it knows. The provider
//      remains the only thing that decides whether a delete is allowed.
//
//   2. IT COSTS ANOTHER NETWORK CALL. Gone. `recordCount` arrives on the zone
//      the modal was already handed, in the same selection set the list and
//      detail pages already read. There is no second call to make.
//
//   3. IT CAN DISAGREE WITH THE PROVIDER. ⚠️ STILL TRUE, AND IT IS WHY THIS
//      FUNCTION IS SEPARATE FROM zoneDeletionBlock(). A count read at page
//      load is stale the moment someone deletes the last record in another
//      tab. If that staleness could BLOCK a delete, a user with a legitimately
//      empty zone would be locked out of the product by a number nobody
//      refreshed — strictly worse than today's behaviour, where the attempt at
//      least reaches the authority. So the count never gates anything: it
//      cannot disable the confirm button, cannot return `overridable: false`,
//      and cannot make the modal predict a failure. A stale count that is
//      wrong costs the user one honest sentence and an attempt that succeeds.
//
// INFORM, DO NOT BLOCK. That is the whole rule, and it is why the return type
// below carries no `overridable` flag — there is nothing here to override.

/**
 * What the dialog should SAY about how full this zone is, if anything.
 *
 * ⚠️ NOT A GUARD. `zoneDeletionBlock` below decides whether a delete may be
 * attempted; this decides only what the operator is told before they choose.
 * Nothing that reads this may disable a control on the strength of it.
 */
export type ZoneContentsNotice = {
    // Machine-readable so the modal branches rather than string-matching.
    reason: 'HOLDS_RECORDS',
    title: string,
    // Observed state and the provider's rule, in that order. NEVER a
    // prediction — see `zoneContentsNotice` for why the wording matters.
    explanation: string,
    // The count as the provider reported it, for a caller that wants to render
    // it separately. Always a real number here: a NOT_REPORTED zone produces
    // no notice at all.
    count: number,
}

/**
 * What is worth saying about this zone's contents before a delete.
 *
 * Returns `undefined` for BOTH a genuinely empty zone and one whose provider
 * reported no count — but the two are undefined for opposite reasons, and the
 * caller must keep them apart:
 *
 *   COUNTED 0      — positive evidence of emptiness. Nothing to warn about.
 *   NOT_REPORTED   — no evidence at all. ⚠️ Saying nothing is correct here;
 *                    saying "this zone is empty" would be an invention. The
 *                    modal has its own copy for this state, because silence in
 *                    a dialog that discusses record counts reads as zero.
 */
export const zoneContentsNotice = (zone?: DnsZone): ZoneContentsNotice | undefined => {

    if (!zone) {
        return undefined;
    }

    // Branched through recordCountState(), never the raw number — the same
    // discipline delegationState() gets next door, and for a sharper reason:
    // the third state here is a number's falsy value. `!zone.recordCount`
    // would treat a real 7-record zone and an uncounted one alike the moment
    // the count is 0, and `zone.recordCount ?? 0` would tell an operator that
    // a zone nobody counted is safe to delete.
    const state = recordCountState(zone);

    if (state.kind !== 'COUNTED' || state.count === 0) {
        return undefined;
    }

    return {
        reason: 'HOLDS_RECORDS',
        title: I18n.get('This zone holds records'),
        // ⚠️ "CURRENTLY HOLDS" AND "REFUSES WHILE THEY EXIST" — observed state,
        // then the provider's standing rule. NOT "this delete will fail":
        // reason 3 above means a record could be gone by the time the button
        // is clicked, and a dialog that predicted a failure which then did not
        // happen would teach the operator to disbelieve it.
        //
        // Two whole sentences rather than one with a plural suffix bolted on.
        // "1 record(s)" is the tell of a dialog nobody proof-read, and this is
        // the copy standing between an operator and a destructive action.
        // Substituting AFTER I18n.get keeps the lookup key stable, the same
        // order productCopy() uses and for the same reason.
        // ⚠️ FNP-1461 — THE DEFAULTS ARE NAMED, BECAUSE EVERY ZONE HAS THEM.
        //
        // This sentence used to end "and the provider refuses to delete a zone
        // while it still holds records", which is false. Route 53's rule is
        // that a zone deletes "only if it has no records other than the
        // default SOA and NS records" — it removes that pair itself. Every
        // zone therefore reports at least 2, so the old copy told an operator
        // deleting a zone they had just created that the provider would refuse,
        // moments before it did not.
        //
        // ⚠️ STILL NOT A PREDICTION, and now in more than one phrasing. The
        // count is a snapshot and the provider remains the authority, so this
        // states the rule and leaves the outcome to the attempt. The previous
        // copy was caught by no test because the guard screened for "will
        // fail" and "cannot be deleted" while the lie shipped as "refuses
        // while records exist" — a regex assertion only catches the wording
        // someone thought of.
        explanation: state.count === 1
            // ⚠️ A COUNT OF 1 IS BELOW THE FLOOR A ROUTE 53 ZONE NORMALLY SITS
            // AT, since every zone is created with an SOA and an NS. It
            // happens — an apex NS set can be removed — so the copy states the
            // rule without asserting which record this one is.
            ? I18n.get('This zone currently holds 1 record. Route 53 will not delete a zone that holds records beyond the default SOA and NS it creates each zone with.')
            : I18n.get('This zone currently holds {count} records, including the default SOA and NS records every zone is created with. Route 53 will not delete a zone that holds records beyond those two.')
                .split('{count}').join(String(state.count)),
        count: state.count,
    };
};

export type ZoneDeletionBlock = {
    // Machine-readable so the modal branches rather than string-matching.
    reason: 'DELEGATED' | 'DELEGATION_UNDETERMINED',
    title: string,
    // Why deleting this zone would hurt, in terms of what breaks.
    explanation: string,
    // ⚠️ Whether an operator may proceed anyway.
    //
    // A DELEGATED zone is a HARD stop: live traffic resolves through it.
    // An UNDETERMINED one is a SOFT stop — "we could not tell" is not evidence
    // of danger, and refusing outright would make undetermined zones
    // permanently undeletable through the UI, which is worse than the risk.
    overridable: boolean,
}

/**
 * Whether deleting this zone should be refused or warned about, on the
 * evidence Flexi holds locally.
 *
 * Returns `undefined` when there is no local objection — which is NOT a promise
 * that the delete will succeed. The provider still owns ZONE_NOT_EMPTY.
 */
export const zoneDeletionBlock = (zone?: DnsZone): ZoneDeletionBlock | undefined => {

    if (!zone) {
        return undefined;
    }

    // Branched through delegationState(), never the raw boolean. `false` and
    // `null` are DIFFERENT answers and the whole read half is built on not
    // collapsing them — a `!zone.appearsDelegated` test here would treat "not
    // determined" as "safe to delete", which is the exact inversion of the
    // truth: undetermined is the case we know least about.
    const state = delegationState(zone);

    if (state === 'DELEGATED') {
        return {
            reason: 'DELEGATED',
            title: I18n.get('This zone is live'),
            explanation: productCopy('The registrar nameservers point at this provider, so this zone is answering DNS for real traffic right now. Deleting it takes every hostname in it offline immediately, and the records cannot be recovered from {product}. Repoint the domain at its new nameservers first, and delete this zone once it is no longer authoritative.'),
            // A hard stop. Not a confirmation an operator can click through in
            // the same gesture that opened the modal.
            overridable: false,
        };
    }

    if (state === 'UNDETERMINED') {
        return {
            reason: 'DELEGATION_UNDETERMINED',
            title: I18n.get('Delegation could not be determined'),
            // ⚠️ States the LIMIT of what is known rather than implying danger.
            // `appearsDelegated` is a read of the provider's published
            // nameservers, not a live resolver query, so null legitimately
            // happens in the minutes after a delegation change.
            explanation: productCopy('{product} could not tell whether this zone is serving live traffic — this is not the same as knowing it is idle. If the domain still points here, deleting the zone takes it offline. Check the domain\'s nameservers at its registrar before continuing.'),
            overridable: true,
        };
    }

    // NOT_DELEGATED. Determined, and the registrar does not point here — the
    // one case with positive evidence that deleting is safe from Flexi's side.
    return undefined;
};
